import { Button, Icons } from '@workflow/ui'
import { useProfile, useSession, useSetAvailability, useSignOut } from '@/lib/api/hooks'
import { ProfileForm } from '@/features/account/ProfileForm'
import { AvailabilityPanel } from '@/features/portal/Availability'
import { errorSentence } from '@/lib/api/client'
import { GateFrame } from './GateFrame'

/**
 * The waiting rooms, and the doors that are closed.
 *
 * `invited` lands on the profile form: it is what the review is of, so it is
 * the only thing asked of a new player or staff member. `pending` lands on a
 * quiet holding page whose only live property is the session probe behind it
 * — the page becomes the console the moment an administrator approves, with
 * no refresh and no news to deliver. The blocked statuses land on the same
 * frame with different words, because the treatment is the point: still the
 * same program, door closed.
 */

const GATE_COPY: Record<'pending' | 'suspended' | 'revoked' | 'rejected', { title: string; body: string }> = {
  pending: {
    title: 'In review',
    body: 'Your profile is in. An administrator reviews new people before the studio opens to them. This page becomes the console the moment that happens.',
  },
  suspended: {
    title: 'Suspended',
    body: 'This account is suspended for now. Ask an administrator if that is unexpected.',
  },
  revoked: {
    title: 'Access revoked',
    body: 'Access to this studio has been revoked. Ask an administrator if that is unexpected.',
  },
  rejected: {
    title: 'Request not approved',
    body: 'The profile that was submitted was not approved. Ask an administrator if that is unexpected.',
  },
}

/**
 * A refused sign-in, as its own page in the gate's voice — not a note on the
 * sign-in cover. The OAuth callback arrives here with a code and, when it
 * knows who was refused, the address itself; the page says what happened and
 * hands the way back.
 */
const REFUSAL_COPY: Record<string, { title: string; body: string }> = {
  notAllowlisted: {
    title: 'Not on the list',
    body: 'Google proved who you are, but this address is not on the allowlist. Ask an administrator to add it.',
  },
  suspended: {
    title: 'Suspended',
    body: 'This account is suspended for now. The profile and the history stay; ask an administrator if that is unexpected.',
  },
  revoked: {
    title: 'Access revoked',
    body: 'Access to this studio has been revoked. Nothing was deleted — ask an administrator if that is unexpected.',
  },
  rejected: {
    title: 'Not approved',
    body: 'The profile that was submitted was not approved. An administrator can reopen the door by inviting the address again.',
  },
  expired: {
    title: 'The attempt expired',
    body: 'Sign-in attempts are short on purpose, and this one took too long or was already used. Start again — the door is where it was.',
  },
  incomplete: {
    title: 'The attempt arrived incomplete',
    body: 'The sign-in lost a piece on its way back from Google. Start again from the door.',
  },
  wrongBrowser: {
    title: 'A different browser finished the attempt',
    body: 'The sign-in started in one browser and finished in another, so it was refused. Begin again where you began.',
  },
  refused: {
    title: 'Google refused the sign-in',
    body: 'The exchange with Google did not go through. Start again; if it keeps happening, ask an administrator.',
  },
  unverifiable: {
    title: 'The sign-in could not be verified',
    body: 'What came back from Google could not be proven ours. Start again; if it keeps happening, ask an administrator.',
  },
}

export function SignInRefusedScreen({ code, email }: { code: string; email: string | null }) {
  const copy = REFUSAL_COPY[code] ?? REFUSAL_COPY.incomplete!
  return (
    <GateFrame width="max-w-[26rem]">
      <h1 className="wf-heading text-foreground">{copy.title}</h1>
      <p className="mt-3 text-ui leading-relaxed text-muted-foreground">{copy.body}</p>
      {email ? <p className="mt-2 text-caption text-muted-foreground">{email}</p> : null}
      <Button size="sm" className="mt-6" icon={<Icons.back />} onClick={() => window.location.assign('/')}>
        Back to the door
      </Button>
    </GateFrame>
  )
}

/**
 * An address that names nothing, met before the door. The same words the
 * console's own 404 carries, on the gate's ground. Query strings are not
 * part of the address and never land here — a refused sign-in's
 * `?authError=…` is read where it belongs, one branch above.
 */
export function NotFoundGateScreen() {
  return (
    <GateFrame width="max-w-[26rem]">
      <h1 className="wf-heading text-foreground">Nothing here.</h1>
      <p className="mt-3 text-ui leading-relaxed text-muted-foreground">
        The address does not name a page of this studio. The surfaces are Home, Jobs, Clients, People and Settings.
      </p>
      <Button size="sm" className="mt-6" icon={<Icons.back />} onClick={() => window.location.assign('/')}>
        Back to the door
      </Button>
    </GateFrame>
  )
}

export function ProfileSetupScreen({ email }: { email: string }) {
  const profile = useProfile()
  const availability = useSetAvailability()
  const session = useSession()
  const signOut = useSignOut()
  // The clock is a player's concern — staff and administrators hold no clock.
  const isPlayer = session.data?.account?.role === 'player'

  return (
    <GateFrame width="max-w-[36rem]">
      <section className="rounded-2xl border border-hairline bg-surface shadow-modal">
        <div className="relative h-48 overflow-hidden rounded-t-2xl bg-[#11131a] sm:h-60">
          <img
            src="/art/onboarding-shoreline.png"
            alt=""
            aria-hidden="true"
            className="size-full object-cover object-center"
          />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-surface" />
        </div>
        <div className="px-5 pb-2 sm:px-8">
          <div className="pb-5 text-center">
            <h1 className="wf-heading text-foreground">Set Up Your Profile</h1>
            <p className="mt-2 text-ui text-muted-foreground">
              Welcome, <span className="text-foreground">{email}</span>. Add your details one section at a time. An administrator reviews your profile before access opens.
            </p>
          </div>
          <ProfileForm initial={profile.data ?? null} guidedSteps
            finalStep={isPlayer ? {
              title: 'Availability',
              pending: availability.isPending,
              content: <AvailabilityPanel onboarding current={session.data!.account!.availability}
                pending={availability.isPending}
                onChange={(value) => availability.mutate({ availability: value })}
                error={availability.error ? errorSentence(availability.error) : undefined} />,
            } : undefined} />
        </div>
      </section>
      <Button size="sm" variant="ghost" className="mt-2" icon={<Icons.signOut />} busy={signOut.isPending} onClick={() => signOut.mutate(undefined)}>
        Sign out
      </Button>
    </GateFrame>
  )
}

export function AccountGateScreen({ kind, email }: { kind: 'pending' | 'suspended' | 'revoked' | 'rejected'; email: string }) {
  const signOut = useSignOut()
  const copy = GATE_COPY[kind]

  return (
    <GateFrame>
      <h1 className="wf-heading text-foreground">{copy.title}</h1>
      <p className="mt-2 text-ui text-muted-foreground">{copy.body}</p>
      <p className="mt-1 text-caption text-muted-foreground">{email}</p>
      <Button
        size="sm"
        className="mt-6"
        icon={<Icons.signOut />}
        busy={signOut.isPending}
        onClick={() => signOut.mutate(undefined)}
      >
        Sign out
      </Button>
    </GateFrame>
  )
}

